import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import * as PIXI from "pixi.js";
import { claimVec2, passVec2 } from "../../../shared/box2dUtils";
import { Antibody, BloodCell, ColliderShape, Cytokine, ElementInfo, Hoverable, Infected, IsBeingDragged, Moved, DragDropProtein, Ownership, Physics, Placeable, PreviewOnly, Renderable, Submerged, Tagger, Transform, Virus } from "../../../shared/components";
import { getEntityPlayer, roundPositionToCompStep, roundRotationToCompStep, validatePlacementOrCombination } from "../../../shared/composition";
import { ControlConstants, MouseControls, RenderEffects, ShopConstants } from "../../../shared/constants";
import { Debug } from "../../../shared/debug";
import { destroyEntity, destroyEntityImmediate, getEntityById, getId } from "../../../shared/entityFactory";
import { getEntityDetails } from "../../../shared/entityTrace";
import { GameElement } from "../../../shared/gameElementTypes";
import { cellLocations } from "../../../shared/generated/assetLocations";
import { HighlightType } from "../../../shared/highlightType";
import { ClientAction, MoveCell, ShopForceMove } from "../../../shared/messageTypes";
import { Sound } from "../../../shared/sound";
import { EntityVFXSystem } from "./entityVFXSystem";
import { addComponent, angleBetween, angleFromUp, getSystem, metersToPixels, pixelsToMeters, rotateAround, screenToWorldPosition, SimpleTransform, vecToStr, worldToScreenPosition, ExecutesEvenIfPaused } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { RenderEngine } from "../rendering/renderEngine";
import { drawArrow } from "../rendering/ShapePrimativeFactory";
import { getIngameTutorial } from "../userSettings";
import { ParticleSystem } from "./particleSystem";
import { SoundSystem } from "./soundSystem";

type Interaction = PIXI.interaction.InteractionEvent;
type SpriteWithComponents = Renderable['sprite'] & {
	physics: Physics,
	transformComponent: Transform,
	renderComponent: Renderable,
	startPosition: vec2,
	//compositionElement?: CompositionElement,
	entity: Entity,
	type: GameElement,
};

type PlacementEvent = {
	context: any,
	eventName: string,
	target: 'immuneCell' | 'bloodCell' | 'rotateHandle' | 'document';
	directionIndicator?: PIXI.Graphics,
	button?: number,
}

export class PlacementSystem extends System implements ExecutesEvenIfPaused {

	private engine: RenderEngine;
	private interactionManager!: PIXI.interaction.InteractionManager;
	private stage!: PIXI.Container;
	private activePlayer: number | null = null;
	private gameClient: GameClient;
	private currentlyDragged: SpriteWithComponents | null = null;
	private soundSystem!: SoundSystem;
	private particleSystem!: ParticleSystem;
	private entityVFXSystem!: EntityVFXSystem;

	private placementEvents = new Array<PlacementEvent>();
	private marketUsingMouseThisFrame = false;
	private forceDragThisEntity: Entity | null = null;

	//Rotation handles
	private directionIndicator!: PIXI.Graphics;
	private groupDirectionIndicator!: PIXI.Graphics;

	//Rotation
	private currentlyRotatable: SpriteWithComponents | null = null;
	private isRotating = false;
	private isRotatingGroup = false;

	//Selection group
	private currentlySelected: SpriteWithComponents[] = [];
	private currentlySelectedCenterPoint = vec2.zero.copy();
	private currentGroupRotationAngle = 0;
	private lastGroupRotationAngle = 0;

	/**When the selection group is closed clicking on an unselected element clears selection.
	 * This is used after a group has been picked out by box select, it feels bad to click something 
	 * else and have it added to the group by multiselection
	 */
	private selectionGroupClosed = false;

	//Box selection
	private boxSelectionStart = vec2.zero.copy();
	private boxSelectionEnd = vec2.zero.copy();

	//Global mouse events and PIXI events reconciliation
	private boxSelectionActive = false
	private mouseUpOverElementThisFrame = false;
	private mouseDownOverElementThisFrame = false;

	//Blood cell special case
	private bloodCellSelected: SpriteWithComponents | null = null;
	private bloodCellHighlight = HighlightType.Highlight;

	//Deferred Moved removals, see removeMovedNextFrame
	private frameNumber = 0;
	private pendingMovedRemovals = new Array<{ entity: Entity, id: number, frame: number }>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.gameClient = attributes.game;
		this.engine = this.gameClient.renderEngine;
	}

	async init() {
		this.soundSystem = getSystem(this.world, SoundSystem);
		this.particleSystem = getSystem(this.world, ParticleSystem);
		this.entityVFXSystem = getSystem(this.world, EntityVFXSystem);
		this.interactionManager = this.engine.pixiApp.renderer.plugins.interaction;
		this.stage = this.engine.sceneRoot;

		//Tell PIXI to capture mouse movements on the parent layer, so we don't get 
		//blocked by any other DOM elements that come before the canvas.
		const target = document.querySelector('.mousesource') as any;
		target.width = target.offsetWidth; //this seems needed cause pixi tries to read these?
		target.height = target.offsetHeight;

		if (target) {
			this.interactionManager.setTargetElement(target as HTMLElement);
		}

		this.directionIndicator = new PIXI.Graphics();
		this.groupDirectionIndicator = new PIXI.Graphics();
		for (const indicator of [this.directionIndicator, this.groupDirectionIndicator]) {
			drawArrow(indicator, RenderEffects.DirectionIndicatorSideLength);
			indicator.interactive = true;
			indicator.buttonMode = true;
			indicator
				.on('pointerdown', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointerdown', 'rotateHandle', event.data.button);
				})
				.on('pointerup', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointerup', 'rotateHandle', event.data.button);
				})
				.on('pointerupoutside', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointerupoutside', 'rotateHandle', event.data.button);
				})
				.on('pointermove', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointermove', 'rotateHandle', event.data.button);
				})
				.on('pointerover', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointerover', 'rotateHandle', event.data.button);
				})
				.on('pointerout', (event: Interaction) => {
					this.marshallEvent(indicator, 'pointerout', 'rotateHandle', event.data.button);
				});
		}

		document.addEventListener('mousedown', (ev) => {
			if (ev.button === MouseControls.LeftButton) {
				this.marshallEvent(ev, 'mousedown', 'document', ev.button);
			}
		});

		document.addEventListener('mouseup', (ev) => {
			if (ev.button === MouseControls.LeftButton) {
				this.marshallEvent(ev, 'mouseup', 'document', ev.button);
			}
		});

		document.addEventListener('mousemove', (ev) => {
			this.marshallEvent(ev, 'mousemove', 'document', ev.button);
		});
	}

	setActivePlayer(id: number | null) {
		this.activePlayer = id;

		//If active player changes that probably means a round change, clear the preview if there is one
		if (this.currentlyDragged) {
			if (this.currentlyDragged.entity && this.currentlyDragged.entity.hasComponent(PreviewOnly)) {
				destroyEntityImmediate(this.currentlyDragged.entity);
			}
			this.currentlyDragged.interactive = false;
			this.abandonDrag();
		}
	}

	forceDragEntity(entity: Entity) {
		this.forceDragThisEntity = entity;
		this.marketUsingMouseThisFrame = false
	}

	signalUIInterceptThisFrame() {
		if (!this.forceDragThisEntity) {
			this.marketUsingMouseThisFrame = true;
			if (this.gameClient.hoveredEntity) {
				this.onLeave();
			}
			if (this.currentlyDragged !== null) {
				this.onPointerUp(this.currentlyDragged);
			}
		}
	}

	isMultiselecting() {
		return this.currentlySelected.length > 1;
	}

	claimDraggedEntityForSell() {
		if (this.currentlyDragged === null || this.isMultiselecting()) return null;
		const claimedSprite = this.currentlyDragged;

		if (claimedSprite.entity === null) {
			console.warn('Tried to sell entity that was already destroyed');
			return null;
		}

		if (claimedSprite.entity.hasComponent(PreviewOnly)) {
			return null;
		}

		this.abandonDrag();
		this.clearSelection();
		claimedSprite.interactive = false;
		return claimedSprite.entity;
	}

	getCurrentlyDraggedEntity() {
		if (this.currentlyDragged !== null && this.isSpriteValid(this.currentlyDragged)) {
			return this.currentlyDragged.entity;
		}
		return null;
	}

	handleShopInitiatedMovement(forcedMove: ShopForceMove) {
		const entity = getEntityById(forcedMove.id);
		const physics = entity?.getComponent(Physics)
		const transform = entity?.getComponent(Transform);
		if (physics) { //could have physics defined but no body or transform if entity was created just this frame
			physics.initialPosition = new vec2(forcedMove.position);
			physics.initialRotation = forcedMove.rotation;
		}
		if (entity && transform && physics) {
			const originalPosition = new vec2(forcedMove.position);

			//If it hadn't been moved locally no need to update it again
			if (originalPosition.equals(transform.position) && forcedMove.rotation === transform.rotation) {
				return;
			}

			transform.position.xy = originalPosition.xy;
			transform.rotation = forcedMove.rotation;

			this.addMovedUntilNextFrame(entity, forcedMove.id);

			//For merged bodies like blood cells, the physics position might be shared and not need updating even if the transform does
			const currentPhysicsPos = claimVec2(physics.body.GetPosition());
			const originalPhysicsPosition = new vec2(forcedMove.physicsPosition);
			if (currentPhysicsPos.equals(originalPhysicsPosition) && forcedMove.rotation === physics.body.GetAngle()) {
				return;
			}

			physics.body.SetTransform(passVec2(originalPhysicsPosition), forcedMove.rotation);

			if (entity.hasComponent(BloodCell)) {
				const player = this.gameClient.players.find(p => p.id === forcedMove.playerId);
				if (player) player.bloodCellCenterPoint = forcedMove.physicsPosition;
			}

		}
	}

	execute(delta: number, time: number) {
		this.processPendingMovedRemovals();
		this.dropStaleSprites();

		if (this.activePlayer !== null) {
			if (this.queries.antibodies.added || this.queries.infected.added || this.queries.blood.added || this.queries.virus.added) {
				const infected = this.queries.infected.added || [];
				const antibodies = this.queries.antibodies.added || [];
				const blood = this.queries.blood.added || [];
				let all = [...antibodies, ...infected, ...blood];
				if (Debug.AllowVirusInShop && this.queries.virus.added) {
					all = all.concat(this.queries.virus.added)
				}
				all.forEach((entity) => {
					const owned = entity.getComponent(Ownership);
					if (owned.playerId === this.activePlayer) {
						//Make sure is not enemy cell. Probably should be a better way to do this.
						const player = this.gameClient.getPlayer()!;
						const isEnemyCell = player.enemyCells.find(cell => cell.id === getId(entity))
						if (!isEnemyCell) {
							addComponent(entity, Placeable);
						}
					}
				});
			}

			this.queries.blood.added?.forEach(entity => {
				const sprite = this.entityToSpriteWithComponents(entity);
				sprite.interactive = true;
				sprite.buttonMode = true;
				sprite.on('pointerdown', (event: Interaction) => { this.marshallEvent(sprite, 'pointerdown', 'bloodCell', event.data.button) });
			});

			this.queries.placeable.added?.forEach(entity => {
				const sprite = this.entityToSpriteWithComponents(entity);
				sprite.interactive = true;
				sprite.buttonMode = true;

				sprite
					.on('pointerdown', (event: Interaction) => { this.marshallEvent(sprite, 'pointerdown', 'immuneCell', event.data.button) })
					.on('pointerup', (event: Interaction) => { this.marshallEvent(sprite, 'pointerup', 'immuneCell', event.data.button) })
					.on('pointerupoutside', (event: Interaction) => { this.marshallEvent(sprite, 'pointerupoutside', 'immuneCell', event.data.button) })
					.on('pointermove', (event: Interaction) => { this.marshallEvent(sprite, 'pointermove', 'immuneCell', event.data.button) })

				if (entity === this.forceDragThisEntity) {
					const event = new PIXI.interaction.InteractionEvent();
					event.data = new PIXI.interaction.InteractionData();
					event.data.button = -1; //this is what pixi does I think
					event.currentTarget = sprite;
					sprite.emit('pointerover', event);
					sprite.emit('pointerdown', event);
					this.forceDragThisEntity = null;
					//Bugfix: Rare circumstance where mouse over shop and and preview start happen on same frame can cause a situation where
					//the document mousedown effect occurs and isn't suppressed by the marketUsingMouseThisFrame flag. So, set this flag here 
					//instead to make sure it knows an element mouse event is handling this.
					this.mouseDownOverElementThisFrame = true;
				}
			});
		}

		this.queries.placeable.removed?.forEach(entity => {
			if (!entity.hasComponent(Renderable)) return; //mostly for things about to be promoted
			const sprite = this.entityToSpriteWithComponents(entity);
			sprite.interactive = false;
			sprite.buttonMode = false;
		});

		//Track hovers over everything in simulation state
		this.queries.hoverable.added?.forEach(entity => {
			const sprite = this.entityToSpriteWithComponents(entity);
			sprite.interactive = true;
			sprite
				.on('pointerover', (event: Interaction) => { this.marshallEvent(sprite, 'pointerover', 'immuneCell', event.data.button) })
				.on('pointerout', (event: Interaction) => { this.marshallEvent(sprite, 'pointerout', 'immuneCell', event.data.button) })
		});

		//Handle all the marsheled mouse events from this last frame
		this.processPlacementEvents();

		//Check for mouse events 
		this.mouseUpOverElementThisFrame = false;
		this.mouseDownOverElementThisFrame = false;

		this.updateRotatable();
		if (this.boxSelectionActive) {
			this.updateSelectionBox()
		}

		this.marketUsingMouseThisFrame = false;
		this.placementEvents = [];

		//Visual update for blood cells since they move as a group
		const bloodCellHighlight = this.bloodCellSelected ? this.bloodCellHighlight : HighlightType.None;
		const bloodCells = this.getPlayerBloodCells();
		for (const sprite of bloodCells) {
			if (!sprite.entity.hasComponent(Submerged)) {
				sprite.renderComponent.highlight = bloodCellHighlight;
			}
		}

		++this.frameNumber;
	}

	/**Add Moved now and remove it on a later frame. This ensures
	 * the Moved component is present for at least one frame and that the removed event works normally
	*/
	addMovedUntilNextFrame(entity: Entity, id: number) {
		addComponent(entity, Moved);
		this.pendingMovedRemovals.push({ entity, id, frame: this.frameNumber });
	}

	private processPendingMovedRemovals() {
		if (this.pendingMovedRemovals.length === 0) return;
		const stillPending = new Array<{ entity: Entity, id: number, frame: number }>();
		for (const pending of this.pendingMovedRemovals) {
			if (pending.frame >= this.frameNumber) {
				stillPending.push(pending);
				continue;
			}

			if (getEntityById(pending.id) === pending.entity && pending.entity.hasComponent(Moved)) {
				pending.entity.removeComponent(Moved);
			}
		}
		this.pendingMovedRemovals = stillPending;
	}

	/** Only the queued mouse events, which mid battle (the only time it's paused) are just hovering, so hover info keeps working */
	executeEvenIfPaused() {
		this.processPlacementEvents();
		this.placementEvents = [];
	}

	processPlacementEvents() {
		//Handle only hover events when not in shop state
		//Sprites of destroyed entities can still have events queued from earlier this frame
		this.placementEvents = this.placementEvents.filter(event =>
			(event.target !== 'immuneCell' && event.target !== 'bloodCell') || this.isSpriteValid(event.context)
		);

		if (this.gameClient.inSimulation || this.gameClient.getCurrentState() !== 'shop') {
			for (const event of this.placementEvents) {
				if (event.target === 'immuneCell') {
					if (event.eventName === 'pointerover') {
						this.onHover(event.context);
					}
					else if (event.eventName === 'pointerout') {
						this.onLeave();
					}
				}
			}
			return;
		}

		for (const event of this.placementEvents) {
			//For now, just bock all non left click events for placement system
			//apparently most PIXI events have button === -1...
			if (event.button !== MouseControls.LeftButton && event.button !== -1) {
				continue;
			};

			switch (event.target) {
				case 'document': {
					switch (event.eventName) {
						case "mouseup": {
							if (this.boxSelectionActive) {
								this.boxSelectionActive = false;
								this.boxSelectionStart = vec2.zero.copy();
								this.boxSelectionEnd = vec2.zero.copy();
								if (this.currentlySelected.length > 0) {
									this.selectionGroupClosed = true;;
								}
							}
							else if (this.currentlyDragged !== null) {
								this.onPointerUp(this.currentlyDragged);
							}
							else {
								if (!this.mouseUpOverElementThisFrame) {
									this.onPointerUpNoElement();
								}
							}
							break;
						}
						case "mousedown": {
							if (this.marketUsingMouseThisFrame) break;
							if (this.mouseDownOverElementThisFrame) {
								break;
							}
							this.boxSelectionActive = true;
							const mouse = this.getWorldMouseCoords();
							this.boxSelectionStart = mouse.copy();
							this.boxSelectionEnd = mouse.copy();
							break;
						}
						case "mousemove": {
							if (!this.boxSelectionActive) break;
							const mouse = this.getWorldMouseCoords();
							this.boxSelectionEnd = mouse.copy();
							break;
						}
					}
				}
				case 'rotateHandle':
					const indicator = event.context as PIXI.Graphics;
					switch (event.eventName) {
						case "pointerdown": {
							if (this.marketUsingMouseThisFrame) break;
							this.mouseDownOverElementThisFrame = true;
							this.isRotating = true;
							break;
						}
						case "pointerup":
						case "pointerupoutside": {
							this.onRotateEnd(indicator === this.groupDirectionIndicator)
							break;
						}
						case "pointermove": {
							if (this.isRotating) this.onRotate(indicator === this.groupDirectionIndicator);
							break;
						}
						case "pointerover": {
							indicator.tint = HighlightType.Highlight;
							break;
						}
						case "pointerout": {
							indicator.tint = HighlightType.None;
							break;
						}
					}
					break;
				case "immuneCell": {
					if (this.marketUsingMouseThisFrame) break;
					switch (event.eventName) {
						case 'pointerdown':
							this.onPointerDown(event.context)
							break;
						case 'pointerup':
							this.onPointerUp(event.context)
							break;
						case 'pointerupoutside':
							this.onPointerUp(event.context);
							break;
						case 'pointermove':
							this.onDragMove(event.context);
							break;
						case 'pointerover':
							this.onHover(event.context);
							break;
						case 'pointerout':
							this.onLeave();
							break;
					}
					break;
				}
				case 'bloodCell': {
					if (this.marketUsingMouseThisFrame) break;
					switch (event.eventName) {
						case 'pointerdown': {
							const sprite = event.context;
							const owner = sprite.entity && sprite.entity.getComponent(Ownership);
							if (owner && owner.playerId !== this.gameClient.getPlayer()?.id) { //not for yourself since you could be moving blood cells
								this.gameClient.showPlayer(owner.playerId);
							}
							break;
						}
					}
					break;
				}
			}
		}
	}

	onHover = (sprite: SpriteWithComponents) => {

		//sprite.renderComponent.highlight = HighlightType.Highlight;
		this.gameClient.hoveredEntity = getId(sprite.entity);
		if (!this.gameClient.isCurrentlyDragging && this.gameClient.hoveredEntity) {
			if (sprite && sprite.transformComponent && sprite.transformComponent.position) { //hacky defense against crash seen here
				this.soundSystem.playSound(Sound.HoverCell, sprite.transformComponent.position);
			}
		}
		if (this.isRotating === false && sprite.entity.hasComponent(Placeable) && !sprite.entity.hasComponent(PreviewOnly)) {
			this.setRotatable(sprite);
		}
	}

	onLeave = () => {
		this.gameClient.hoveredEntity = null;
	}

	//private selectionGroupRoot = new PIXI.Container();
	select(sprite: SpriteWithComponents) {
		//Blood cell movement is handled differently because of how they group up
		const isBloodCell = sprite.entity.hasComponent(BloodCell);
		if (isBloodCell) {
			this.bloodCellSelected = sprite;
			return;
		}

		this.currentlySelected.push(sprite);
		sprite.renderComponent.highlight = HighlightType.Highlight;

		//move to front
		this.engine.sceneRoot.addChild(sprite);
		this.engine.inSceneUI.addChild(sprite.renderComponent.glowSprite);
	}

	deselect(sprite: SpriteWithComponents, skipRemoveFromList = false) {
		sprite.renderComponent.highlight = HighlightType.None;
		if (!skipRemoveFromList) {
			const index = this.currentlySelected.indexOf(sprite);
			if (index > -1) {
				this.currentlySelected.splice(index, 1);
			}
		}
	}

	clearSelection() {
		for (const sprite of this.currentlySelected) {
			this.deselect(sprite, true);
		};
		this.currentlySelected = [];
		this.selectionGroupClosed = false;
		this.bloodCellSelected = null;
	}

	onPointerDown = (sprite: SpriteWithComponents) => {
		this.mouseDownOverElementThisFrame = true;
		//const sprite = event.currentTarget as SpriteWithComponents;
		const position = sprite.transformComponent.position.copy();
		sprite.renderComponent.addedScale = new vec2([0.1, 0.1]);
		sprite.startPosition = position;

		this.currentlyDragged = sprite;

		this.setErrorHighlightIfInvalidPlacement(sprite);

		this.gameClient.hoveredEntity = null;

		const info = sprite.entity.getComponent(ElementInfo);
		const preview = sprite.entity.getComponent(PreviewOnly);
		const isPreview = preview != undefined;

		this.entityVFXSystem.updatePromotePreview(info.type, sprite.entity, isPreview);

		if (isPreview) {
			this.clearSelection();
		}

		const isBloodCell = sprite.entity.hasComponent(BloodCell);
		if (!this.currentlySelected.includes(sprite) && !(isBloodCell && this.bloodCellSelected)) {
			if (this.selectionGroupClosed) {
				this.clearSelection();
			}
			this.select(sprite);
		}

		if (this.currentlySelected.length > 0) {
			this.gameClient.isCurrentlyDragging = true;
			this.gameClient.setUIState();
		}

		this.gameClient.screenShake(0.15);
	}

	onPointerUpNoElement = () => {
		this.clearSelection();
		this.abandonDrag(); //nothing is dragged once the mouse is up, whatever ended the drag
	}

	onPointerUp = (sprite: SpriteWithComponents, isPointerOutside = false) => {
		this.gameClient.isCurrentlyDragging = false;
		this.entityVFXSystem.updatePromotePreview(null);
		this.gameClient.setUIState();

		//const sprite = event.currentTarget as SpriteWithComponents;

		this.mouseUpOverElementThisFrame = true; //it was over an element so don't do the clear selection mouse up

		if (sprite !== this.currentlyDragged) return;
		this.currentlyDragged = null;


		const transform = sprite.entity.getComponent(Transform);
		if (!transform) {
			console.warn(`Drag end got called on destroyed entity`);
			return;
		}; //Entity was destroyed

		for (const sprite of this.currentlySelected) {
			sprite.entity.removeComponent(Moved);
		}

		const selectedCount = this.currentlySelected.length + (this.bloodCellSelected ? 1 : 0);

		const wasActuallyDragged = sprite.entity.hasComponent(IsBeingDragged);
		if (wasActuallyDragged) {
			sprite.entity.removeComponent(IsBeingDragged);
		}

		//If using multitouch, then we want to keep things selected if they weren't moved, or if there was a group selected already
		if (selectedCount === 1) {
			if (ControlConstants.UseMultitouchSelection && wasActuallyDragged) {
				this.deselect(sprite);
			}
			else if (!ControlConstants.UseMultitouchSelection) {
				this.deselect(sprite);
			}
		}

		this.gameClient.screenShake(0.25);

		const preview = sprite.entity.getComponent(PreviewOnly);

		if (preview) {
			roundPositionToCompStep(sprite.transformComponent.position, sprite.transformComponent.position);
			sprite.transformComponent.rotation = roundRotationToCompStep(sprite.transformComponent.rotation);
			if (preview.onEndPreview !== null) {
				const isValid = validatePlacementOrCombination( //can assume no blood cells cause preview
					this.gameClient.getPlayer()!,
					sprite.transformComponent,
					{ type: sprite.type },
					this.gameClient
				);
				preview.onEndPreview(isValid);
			}
			destroyEntity(sprite.entity);
			sprite.interactive = false; //further interaction can cause problems
			return;
		}

		sprite.renderComponent.addedScale = vec2.zero.copy();

		//Endpoint for dragging groups
		if (selectedCount > 1) {
			this.validateAndUpdateSelectionGroup();
			this.soundSystem.playSound(Sound.PlaceCell, sprite.transformComponent.position);
			return;
		}

		//Endpoint for dragging single elements
		const spawnPoint = new vec2(this.gameClient.getPlayer()!.spawnPoint);
		const pos = transform.position.copy();
		const distanceFromSpawn = vec2.distance(pos, spawnPoint);
		if (distanceFromSpawn > ShopConstants.PlacementRadius && !sprite.entity.hasComponent(BloodCell)) {
			const id = getId(sprite.entity);
			if (!id) throw new Error('PlacementSystem: Tried to sell non existant id');

			const moved = this.moveToValidPlacement(sprite);
			if (moved) {
				this.updatePlacement(sprite);
			}
			this.gameClient.hoveredEntity = null;
		} else {
			this.soundSystem.playSound(Sound.PlaceCell, sprite.transformComponent.position);

			if (this.bloodCellSelected !== null) {
				const moved = this.moveToValidPlacement(this.bloodCellSelected);
				if (moved) {
					const bloodCellSprites = this.getPlayerBloodCells();
					this.updateGroupPlacement(bloodCellSprites);
				}
				this.bloodCellSelected = null;
			}
			else {
				const moved = this.moveToValidPlacement(sprite);
				if (moved) {
					this.updatePlacement(sprite);
				}
			}
		}

	}

	onDragMove = (sprite: SpriteWithComponents) => {
		if (this.currentlyDragged === sprite) {
			this.gameClient.hoveredEntity = null;

			const draggedSprite = this.currentlyDragged;
			const currentPosition = draggedSprite.transformComponent.position;
			if (currentPosition === undefined) { //hacky defense against crash seen here
				this.currentlyDragged = null;
				return;
			};
			let mousePos = this.engine.getMouseCoords();
			mousePos = screenToWorldPosition(mousePos);
			const delta = vec2.difference(mousePos, currentPosition);

			for (const sprite of this.currentlySelected) {
				if (!sprite.entity) continue; //TODO: This seems able to happen, which is bad..
				//Update position
				addComponent(sprite.entity, IsBeingDragged);
				addComponent(sprite.entity, Moved);

				const currentPosition = sprite.transformComponent.position;
				const newPos = currentPosition.copy().add(delta);

				this.setPositionAndRotation(sprite, newPos, null, true);
			}

			if (this.bloodCellSelected !== null) {
				this.bloodCellSelected.transformComponent.position;
				const newPos = currentPosition.copy().add(delta);
				this.setPositionOfBloodCellClump(draggedSprite, this.bloodCellSelected, newPos);
				this.setErrorHighlightIfInvalidPlacement(draggedSprite);
			}

			//This has to happen after position updates in case only one has moved
			for (const sprite of this.currentlySelected) {
				if (!sprite.entity) continue; //TODO: This seems able to happen, which is bad..
				this.setErrorHighlightIfInvalidPlacement(sprite);
			}

			this.processDragTriggers();
		}
	}

	getWorldMouseCoords() {
		return screenToWorldPosition(this.engine.getMouseCoords());
	}

	findNearestValidPlacement(transform: SimpleTransform, type: GameElement, disallowCombine = false) {
		const player = this.gameClient.getPlayer()!;
		const radius = transform.size.x / 2;
		const spawn = new vec2(player.spawnPoint);
		let fromSpawnPoint = transform.position.copy().subtract(spawn).normalize();
		if (fromSpawnPoint.equals(vec2.zero)) {
			fromSpawnPoint = new vec2([Math.random(), Math.random()]).normalize();
		}
		let pos = transform.position.copy();
		roundPositionToCompStep(pos, pos);
		const placement = { position: pos, rotation: transform.rotation, size: transform.size, shape: transform.shape };
		for (let i = 1;//can assume no blood cells cause always prev position
			!validatePlacementOrCombination(this.gameClient.getPlayer()!, placement, { type }, this.gameClient, { disallowCombine });
			++i
		) {
			pos.add(fromSpawnPoint.copy().scale(Math.floor(i) * radius));
			fromSpawnPoint.negate();
			roundPositionToCompStep(pos, pos);
		}
		return pos;
	}

	private updatePlacement(sprite: SpriteWithComponents, skipMessage = false) {
		//Run this to do actual rounding of position, since we don't do on every drag step
		if (!sprite.entity.hasComponent(BloodCell)) {
			this.setPositionAndRotation(sprite, sprite.transformComponent.position, sprite.transformComponent.rotation);
		}
		else if (this.bloodCellSelected) {
			this.setPositionOfBloodCellClump(sprite, this.bloodCellSelected, sprite.transformComponent.position);
		}

		const physicsPosition = claimVec2(sprite.physics.body.GetPosition()); //could be different in case of joined cells like blood cells
		const position = sprite.transformComponent.position;
		const rotation = sprite.physics.body.GetAngle();
		const compElement = this.gameClient.getEntityCompElement(sprite.entity);
		if (!compElement) {
			console.error(`Entity moved missing comp element: ${getEntityDetails(sprite.entity)}`);
			return null;
		}
		let messageType: ClientAction;
		const info = sprite.entity.getComponent(ElementInfo);
		if (info.type === GameElement.INFECTED_BLOODCELL) {
			messageType = ClientAction.MoveInfected;
		}
		else {
			messageType = ClientAction.MoveAntibody;
		}

		compElement.position = position.xy;
		compElement.rotation = rotation;
		const move: MoveCell = {
			messageType,
			id: compElement.id,
			toPhysicsPosition: physicsPosition.xy,
			toPosition: position.xy,
			toRotation: rotation,
		}
		if (!skipMessage) this.gameClient.connectionToServer?.send(move);

		return move;
	}

	private updateGroupPlacement(group: SpriteWithComponents[] = this.currentlySelected) {
		const moveMessages = new Array<MoveCell>();
		for (const sprite of group) {
			const move = this.updatePlacement(sprite, true);
			if (move !== null) {
				moveMessages.push(move);
			}
		}
		this.gameClient.connectionToServer?.send({
			messageType: ClientAction.MoveCellGroup,
			cells: moveMessages,
		});
	}

	/**Returns true if movement was valid OR if the cell was forcibly moved somewhere else */
	private moveToValidPlacement(sprite: SpriteWithComponents, disallowCombination = false) {
		roundPositionToCompStep(sprite.transformComponent.position, sprite.transformComponent.position);
		const valid = this.validatePlacementAccountingForBloodCells(sprite, disallowCombination);
		let moved = false;
		if (!valid) {
			moved = this.moveToLastOrNearestValidPlacement(sprite);
		}
		return valid || moved;
	}

	/**Returns true if it needed to calculate nearest valid position */
	private moveToLastOrNearestValidPlacement(sprite: SpriteWithComponents) {
		const transform = sprite.transformComponent;
		const isBlood = sprite.entity.hasComponent(BloodCell);

		//Last valid position if possibe
		const comp = this.gameClient.getEntityCompElement(sprite.entity);
		if (comp && comp.position) {
			if (isBlood) {
				this.setPositionOfBloodCellClump(sprite, this.bloodCellSelected!, new vec2(comp.position));
			}
			else {
				this.setPositionAndRotation(sprite, new vec2(comp.position), comp.rotation)
			}

			return false;
		}
		//Otherwise use the nearest one we can find
		else {
			const position = this.findNearestValidPlacement(transform, sprite.type);
			this.setPositionAndRotation(sprite, position);
			return true;
		}

	}

	private setRotatable(sprite: SpriteWithComponents | null) {
		if (sprite == null) {
			this.engine.inSceneUI.removeChild(this.directionIndicator);
			this.isRotating = false;
			this.currentlyRotatable = null;
		}
		else {
			this.directionIndicator.position.set(0, 0);
			this.engine.inSceneUI.addChild(this.directionIndicator);
			this.currentlyRotatable = sprite;
		}
	}

	private onRotateEnd(isGroup = false) {
		this.mouseUpOverElementThisFrame = true;
		if (!this.isRotating) return;
		this.isRotating = false;
		if (this.currentlyRotatable !== null) {
			const sprite = this.currentlyRotatable;

			const moved = this.moveToValidPlacement(sprite, true);
			if (!this.currentlySelected.includes(sprite)) {
				sprite.renderComponent.highlight = HighlightType.None; //TODO: Assumes can't be hovering at the same time!
			}
			sprite.entity.removeComponent(IsBeingDragged);
			if (moved) {
				if (getIngameTutorial()) {
					this.gameClient.tutorialStateMachine.onRotateCell(this.gameClient.getEntityCompElement(sprite.entity)!);
					this.gameClient.updateUI();
				}
				this.updatePlacement(sprite);
			}
		}
		else if (isGroup) {
			this.isRotatingGroup = false;
			const valid = this.validateAndUpdateSelectionGroup();
			if (valid) {
				this.lastGroupRotationAngle = this.currentGroupRotationAngle;
			} else {
				this.currentGroupRotationAngle = this.lastGroupRotationAngle;
			}
		}
	}

	private onRotate(isGroup = false) {
		//Janky defend against the rotate target being destroyed
		if (this.currentlyRotatable && !this.currentlyRotatable.transformComponent?.position) {
			this.setRotatable(null);
			this.isRotating = false;
			return;
		};

		if (this.currentlyRotatable) {
			const sprite = this.currentlyRotatable;
			const mouse = this.engine.getMouseCoords();
			const rotatorPos = new vec2([sprite.position.x, sprite.position.y]);

			const rotationVector = vec2.direction(screenToWorldPosition(mouse), screenToWorldPosition(rotatorPos));
			let targetAngle = angleFromUp(rotationVector);

			this.setPositionAndRotation(sprite, null, targetAngle);

			this.setErrorHighlightIfInvalidPlacement(sprite, true);

			addComponent(sprite.entity, IsBeingDragged);
			return;
		}

		if (isGroup) {
			//Defend against destroyed selected
			if (!this.checkSelectedElementsNotDestroyed()) {
				this.isRotating = false;
				this.clearSelection();
				return;
			}

			this.isRotatingGroup = true;
			const mouse = this.getWorldMouseCoords();
			const rotationVector = vec2.direction(mouse, this.currentlySelectedCenterPoint);
			const startingRotationVector = new vec2([0, 1]);
			rotateAround(startingRotationVector, this.lastGroupRotationAngle, vec2.zero, startingRotationVector);
			const targetAngle = angleBetween(startingRotationVector, rotationVector);

			for (const sprite of this.currentlySelected) {
				const compElement = this.gameClient.getEntityCompElement(sprite.entity);
				if (!compElement) continue;
				const startingPosition = new vec2(compElement.position);
				const destination = rotateAround(startingPosition, targetAngle, this.currentlySelectedCenterPoint);
				const rotation = compElement.rotation + targetAngle;
				this.setPositionAndRotation(sprite, destination, rotation);
				this.setErrorHighlightIfInvalidPlacement(sprite, true);
			}

			this.currentGroupRotationAngle = targetAngle + this.lastGroupRotationAngle;

			return;
		}
	}

	private updateRotatable() {
		//Janky defend against the rotate target being destroyed
		if (this.currentlyRotatable && !this.currentlyRotatable.transformComponent?.position) {
			this.setRotatable(null);
			this.isRotating = false;
			return;
		};


		//OPT don't think we need this every frame?
		if (this.currentlySelected.length > 1 && !this.bloodCellSelected) {
			//Defend against destroyed selected
			if (!this.checkSelectedElementsNotDestroyed()) {
				this.isRotating = false;
				this.clearSelection();
				return;
			}

			//Calculate average position of selected elements
			const averagePosition = vec2.zero.copy();
			let maxDistance = 0;
			for (const sprite of this.currentlySelected) {
				averagePosition.add(sprite.transformComponent.position);
			}
			averagePosition.scale(1 / this.currentlySelected.length);

			//Update the centerpoint only if not rotating. If we are rotating, the movement of the 
			//units the centerpoint is derived from can cause it to sublty drift.
			if (!this.isRotating) {
				averagePosition.copy(this.currentlySelectedCenterPoint)
			}

			//Calculate a bounding circle around all the selected elements using a for-of loop
			for (const sprite of this.currentlySelected) {
				const distance = vec2.distance(sprite.transformComponent.position, averagePosition);
				if (distance > maxDistance) maxDistance = distance;
			}

			const screenAveragePos = this.engine.getScreenCoordinate(worldToScreenPosition(averagePosition));

			//Draw bounds
			let padding = cellLocations.wall.width / 2 + 10; //idk, probably the biggest thing that could run over the circle...
			const boundRadius = (metersToPixels(maxDistance) + padding) * this.engine.sceneRoot.scale.x;
			this.engine.graphicsOverlay.lineStyle(2, 0xffffff, 1.0);
			this.engine.graphicsOverlay.drawCircle(screenAveragePos.x, screenAveragePos.y, boundRadius);
			this.engine.graphicsOverlay.lineStyle(0);

			//Put the direction indicator outside the bounds
			if (this.groupDirectionIndicator.parent !== this.engine.inSceneUI) this.engine.inSceneUI.addChild(this.groupDirectionIndicator);

			const rotation = this.currentGroupRotationAngle;
			const arrowNormal = new vec2([0, 1]);
			rotateAround(arrowNormal, rotation, vec2.zero, arrowNormal);
			let arrowPos = worldToScreenPosition(this.getWorldMouseCoords());
			if (!this.isRotatingGroup) {
				arrowPos = this.currentlySelectedCenterPoint.copy().add(arrowNormal.scale(maxDistance + pixelsToMeters(padding) + 2));
				arrowPos = worldToScreenPosition(arrowPos);
			}

			this.groupDirectionIndicator.rotation = -this.currentGroupRotationAngle;
			this.groupDirectionIndicator.position.set(...arrowPos.xy);
		} else {
			if (this.groupDirectionIndicator.parent === this.engine.inSceneUI) this.engine.inSceneUI.removeChild(this.groupDirectionIndicator);
			this.currentGroupRotationAngle = 0;
			this.lastGroupRotationAngle = 0;
		}

		if (this.currentlyRotatable && this.currentlyRotatable.entity && this.currentlyRotatable.entity.hasComponent(Transform)) {
			const mouseScreenPos = this.engine.getMouseCoords();
			const mouse = screenToWorldPosition(mouseScreenPos);
			const entityTransform = this.currentlyRotatable.transformComponent;
			const padding = pixelsToMeters(RenderEffects.DirectionIndicatorSideLength);
			if (!this.isRotating && vec2.distance(mouse, entityTransform.position) > entityTransform.size.y + padding * 2) {
				this.onRotateEnd();
				this.setRotatable(null);
				this.isRotating = false;
			}
			else {
				let arrowPos = worldToScreenPosition(this.getWorldMouseCoords());
				if (!this.isRotating) {
					const arrowOffset = entityTransform.size.x === entityTransform.size.y
						? entityTransform.size.x / 2 + pixelsToMeters(RenderEffects.DirectionIndicatorSideLength / 2)
						: entityTransform.size.y;
					arrowPos = entityTransform.position.copy().add(new vec2([0, arrowOffset]))
					arrowPos = rotateAround(arrowPos, entityTransform.rotation, entityTransform.position);
					arrowPos = worldToScreenPosition(arrowPos);
				}

				this.directionIndicator.rotation = entityTransform.getRenderTransform().rotation;
				this.directionIndicator.position.set(...arrowPos.xy);
			}
		}

		//Draw line in direction of rotation
		if (this.isRotating) {
			let origin = this.currentlySelectedCenterPoint;
			if (!this.isRotatingGroup) {
				if (!this.currentlyRotatable || !this.currentlyRotatable.transformComponent) return;
				origin = this.currentlyRotatable.transformComponent.position;
			}
			const end = this.engine.getScreenCoordinate(worldToScreenPosition(this.getWorldMouseCoords()));
			origin = this.engine.getScreenCoordinate(worldToScreenPosition(origin));

			//Starting point
			this.engine.graphicsOverlay.lineStyle(2, 0xffffff, 1.0);

			this.engine.graphicsOverlay.beginFill(0xffffff);
			this.engine.graphicsOverlay.drawCircle(origin.x, origin.y, 4);
			this.engine.graphicsOverlay.endFill();

			//Starting point
			this.engine.graphicsOverlay.moveTo(origin.x, origin.y);
			this.engine.graphicsOverlay.lineTo(end.x, end.y);

			this.engine.graphicsOverlay.lineStyle(0);
		}

		//Scale the indicators up when we zoom out
		this.directionIndicator.scale.set(1 / this.engine.sceneRoot.scale.x);
		this.groupDirectionIndicator.scale.set(1 / this.engine.sceneRoot.scale.x);
	}

	private validateAndUpdateSelectionGroup() {
		const player = this.gameClient.getPlayer()!;
		let groupIsValid = this.currentlySelected.every(sprite => {
			const compElement = this.gameClient.getEntityCompElement(sprite.entity);
			roundPositionToCompStep(sprite.transformComponent.position, sprite.transformComponent.position);
			return validatePlacementOrCombination(
				player,
				sprite.transformComponent,
				compElement || { type: sprite.type },
				this.gameClient,
				{ disallowCombine: true }
			)
		});

		if (this.bloodCellSelected) {
			groupIsValid = groupIsValid && this.validatePlacementAccountingForBloodCells(this.bloodCellSelected, true);
		}

		let groupIds = this.currentlySelected.map(sprite => getId(sprite.entity)) as number[];
		groupIds = groupIds.filter(id => {
			const isNull = id === null;
			if (isNull) console.warn(`Found a null id in placement group. Could lead to invalidated movement`);
			return !isNull;
		}) as number[];
		let hasNewlyComputedPositions = false;
		for (const sprite of this.currentlySelected) {
			//Assumption: A group is always be made of things that had a valid position
			if (!groupIsValid) {
				hasNewlyComputedPositions = hasNewlyComputedPositions || this.moveToLastOrNearestValidPlacement(sprite);
			}
			//We've handled any errors, and we know a groups is active so these should all be normal highlight
			sprite.renderComponent.highlight = HighlightType.Highlight;
		}

		if (!groupIsValid && this.bloodCellSelected) {
			hasNewlyComputedPositions = hasNewlyComputedPositions || this.moveToLastOrNearestValidPlacement(this.bloodCellSelected);
		}

		if (groupIsValid || hasNewlyComputedPositions) {
			if (this.bloodCellSelected !== null) {
				const bloodCellSprites = this.getPlayerBloodCells();
				const spritesToMove = bloodCellSprites.concat(this.currentlySelected);
				this.updateGroupPlacement(spritesToMove);
			}
			else {
				this.updateGroupPlacement(this.currentlySelected);
			}
		}
		return groupIsValid;
	}

	private setErrorHighlightIfInvalidPlacement(sprite: SpriteWithComponents, disallowCombination = false) {
		const valid = this.validatePlacementAccountingForBloodCells(sprite, disallowCombination);
		const isBloodCell = sprite.entity.hasComponent(BloodCell);
		if (!valid) {
			sprite.renderComponent.highlight = HighlightType.Error;
			if (isBloodCell) this.bloodCellHighlight = HighlightType.Error;
		}
		else {
			sprite.renderComponent.highlight = HighlightType.Highlight;
			if (isBloodCell) this.bloodCellHighlight = HighlightType.Highlight
		}
	}

	private setPositionOfBloodCellClump(movedCell: SpriteWithComponents, selectedbloodcell: SpriteWithComponents, position: vec2) {
		const physics = selectedbloodcell.physics;
		const transform = movedCell.transformComponent;

		//Move all associated blood cells along with this
		const delta = position.copy().subtract(transform.position);
		const initialPhysicsPosition = claimVec2(physics.body.GetPosition());
		const newPos = initialPhysicsPosition.add(delta);
		physics.body.SetTransform(passVec2(newPos), 0)

		const player = this.gameClient.getPlayer();
		if (player) player.bloodCellCenterPoint = newPos.xy;

		//Move all the blood cells to a position consistent with their offsets
		const allBloodCells = this.getPlayerBloodCells();
		for (const sprite of allBloodCells) {
			sprite.transformComponent.position.add(delta);
		}
		return;
	}

	private setPositionAndRotation(sprite: { physics: Physics, transformComponent: Transform }, position: vec2 | null = null, rotation: number | null = null, skipRoundPosition = false) {
		const physics = sprite.physics;
		const transform = sprite.transformComponent;

		if (rotation === null) rotation = physics.body.GetAngle();
		if (position === null) position = claimVec2(physics.body.GetPosition());
		transform.position.xy = position.xy;
		if (!skipRoundPosition) roundPositionToCompStep(transform.position, transform.position);
		physics.body.SetTransform(passVec2(transform.position.copy()), rotation)
		transform.rotation = rotation;
	}

	private updateSelectionBox() {
		//Draw it
		const start = this.engine.getScreenCoordinate(worldToScreenPosition(this.boxSelectionStart));
		const end = this.engine.getScreenCoordinate(worldToScreenPosition(this.boxSelectionEnd));
		this.engine.graphicsOverlay.lineStyle(2, 0x777777, 1.0);
		this.engine.graphicsOverlay.drawRect(start.x, start.y, end.x - start.x, end.y - start.y);
		this.engine.graphicsOverlay.lineStyle(0);

		//Find all the elements in the box
		const searchStart = new vec2([Math.min(start.x, end.x), Math.min(start.y, end.y)]);
		const searchEnd = new vec2([Math.max(start.x, end.x), Math.max(start.y, end.y)]);
		const box = new PIXI.Rectangle(searchStart.x, searchStart.y, searchEnd.x - searchStart.x, searchEnd.y - searchStart.y);
		const inSelectionBox = this.queries.placeable.results.filter(entity => {
			const sprite = this.entityToSpriteWithComponents(entity);
			const pos = this.engine.getScreenCoordinate(worldToScreenPosition(sprite.transformComponent.position));
			return box.contains(pos.x, pos.y);
		});

		//delect everything selected outside the box
		for (const sprite of this.currentlySelected) {
			const inBox = inSelectionBox.find(entity => entity === sprite.entity);
			if (!inBox) {
				this.deselect(sprite);
			}
			else {
				inSelectionBox.splice(inSelectionBox.indexOf(inBox), 1);
			}
		}
		this.bloodCellSelected = null;

		//Select anything remaining inside the box
		for (const inBox of inSelectionBox) {
			//if (inBox.hasComponent(BloodCell) && this.bloodCellSelected) continue;
			const sprite = this.entityToSpriteWithComponents(inBox);
			this.select(sprite);
		}
	}

	private entityToSpriteWithComponents(entity: Entity) {
		const renderable = entity.getMutableComponent(Renderable);
		const sprite = renderable.sprite as SpriteWithComponents;
		sprite.entity = entity;
		sprite.renderComponent = renderable;
		sprite.physics = entity.getMutableComponent(Physics);
		sprite.transformComponent = entity.getComponent(Transform);
		const info = entity.getComponent(ElementInfo);
		sprite.type = info.type;
		return sprite;
	}

	/**True if the sprite still belongs to a live entity.*/
	private isSpriteValid(sprite: SpriteWithComponents | null) {
		return sprite !== null && !!sprite.entity && sprite.entity.getComponent(Renderable)?.sprite === sprite;
	}

	private abandonDrag() {
		this.currentlyDragged = null;
		if (this.gameClient.isCurrentlyDragging) {
			this.gameClient.isCurrentlyDragging = false;
			this.entityVFXSystem.updatePromotePreview(null);
			this.gameClient.setUIState();
		}
	}

	private dropStaleSprites() {
		if (this.currentlyDragged !== null && !this.isSpriteValid(this.currentlyDragged)) {
			this.abandonDrag();
		}
		if (this.currentlyRotatable !== null && !this.isSpriteValid(this.currentlyRotatable)) {
			this.setRotatable(null);
		}
		if (this.bloodCellSelected !== null && !this.isSpriteValid(this.bloodCellSelected)) {
			this.bloodCellSelected = null;
		}
		if (this.currentlySelected.some(sprite => !this.isSpriteValid(sprite))) {
			this.currentlySelected = this.currentlySelected.filter(sprite => this.isSpriteValid(sprite));
			if (this.currentlySelected.length === 0) {
				this.selectionGroupClosed = false;
			}
		}
	}

	private checkSelectedElementsNotDestroyed() {
		for (const sprite of this.currentlySelected) {
			if (!sprite.transformComponent || !sprite.transformComponent.position) {
				return false;
			}
		}
		return true;
	}

	private processDragTriggers() {
		//Note that query already checks for IsBeingDragged
		for (const entity of this.queries.dragTriggers.results) {
			const trigger = entity.getComponent(DragDropProtein);
			const transform = entity.getComponent(Transform);
			const entityId = getId(entity);
			if (!entityId) continue

			if (!trigger.lastPosition) {
				trigger.lastPosition = transform.position.copy();
			}

			const distanceTraveled = vec2.distance(trigger.lastPosition, transform.position);
			if (distanceTraveled > trigger.distance) {
				//Since this code runs on client only, instead of triggering the thing here
				//we need to send a message to the shopState on the server. Server then 
				//tells the clientShopState to trigger the thing. So the actual trigger happens
				//in the shop states.
				trigger.lastPosition = transform.position.copy();
				this.gameClient.sendDragTrigger(entityId, transform.position)
			}
		}
	}

	private marshallEvent(sprite: any, eventName: PlacementEvent['eventName'], target: PlacementEvent['target'], button: number) {
		if (this.gameClient.tutorialStateMachine.disableCellClicks) {
			return;
		}

		this.placementEvents.push({
			context: sprite,
			eventName,
			target,
			button
		});
	}


	//TODO: Right now we always move all blood cells at once. This should probably work on connected clumps so they can be seperated later
	private getPlayerBloodCells() {
		const player = this.gameClient.getPlayer();
		if (!player) return [];
		const bloodCells = player.hpCells.map(cell => cell.id);
		return bloodCells.map(id => getEntityById(id)).filter(entity => entity !== null)
			.map(entity => this.entityToSpriteWithComponents(entity!)) as SpriteWithComponents[];
	}

	private validatePlacementAccountingForBloodCells(sprite: SpriteWithComponents, disallowCombine = false) {
		const player = this.gameClient.getPlayer()!;
		let transform: { size: vec2, rotation: number, position: vec2, shape: ColliderShape } = sprite.transformComponent;

		//If this is a bloodcell, we need to validate all connected blood cells
		let excludeList = new Array<number>();
		if (player && sprite.entity.getComponent(BloodCell)) {
			const allBloodCells = this.getPlayerBloodCells(); //TODO: The connected bloodcell part is not implemented, we just check them all
			let isValid = true;
			for (const cell of allBloodCells) {
				const compElement = this.gameClient.getEntityCompElement(cell.entity);
				isValid = isValid && validatePlacementOrCombination(
					player,
					cell.transformComponent,
					compElement || { type: cell.type },
					this.gameClient,
					{ disallowCombine, skipBloodCellOverlap: true }
				);
			}
			return isValid;
		}
		else {
			const compElement = this.gameClient.getEntityCompElement(sprite.entity);
			return validatePlacementOrCombination(
				player,
				transform,
				compElement || { type: sprite.type },
				this.gameClient,
				{ disallowCombine }
			);
		}
	}
}

PlacementSystem.queries = {
	placeable: {
		components: [Renderable, Placeable, Physics, Ownership],
		listen: { added: true, removed: true }
	},
	antibodies: {
		components: [Antibody, Ownership],
		listen: { added: true }
	},
	infected: {
		components: [Infected, Ownership],
		listen: { added: true }
	},
	hoverable: {
		components: [Hoverable],
		listen: { added: true }
	},
	blood: {
		components: [BloodCell],
		listen: { added: true }
	},
	virus: {
		components: [Virus],
		listen: { added: true }
	},
	dragTriggers: {
		components: [DragDropProtein, Transform, Not(PreviewOnly), IsBeingDragged],
	},
	droppedTags: {
		components: [Cytokine, Tagger],
	}
}
